'use client';

import { motion } from 'framer-motion';

import { ISanitizedCompany } from 'types/company';
import { IUserResponse } from 'types/user';

import { gradient } from 'constants/theme';

import { useDarkMode } from 'hooks/browser/mediaQueries';
import { useCreateStory } from 'hooks/stories/firebase/create';

import { Spinner } from 'components/shared/Spinner';

interface IProps {
  company: ISanitizedCompany;
  variant?: 'small' | 'large';
  user?: IUserResponse;
  collectionId?: string;
}

export const NewStoryButton = ({
  company,
  variant = 'small',
  user,
  collectionId,
}: IProps) => {
  const { isLoading, createNewStory } = useCreateStory(company, collectionId);
  const { isDarkMode } = useDarkMode();

  if (variant === 'large') {
    if (!user?.profile?.firstName) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="h-full pt-[30px]"
      >
        <button
          className="mt-3 mb-5 flex aspect-[9/16] w-full flex-col items-center justify-center rounded-xl py-6 px-2  text-mineShaftGrey shadow-md disabled:pointer-events-none disabled:opacity-50 sm:mb-6"
          style={{ backgroundImage: gradient }}
          onClick={createNewStory}
          disabled={isLoading}
          aria-label="Create new Story"
        >
          <div className="pb-1 text-lg font-bold md:text-[26px]">
            Welcome {user.profile.firstName}!
          </div>

          <div className="leading-tight md:text-[22px]">
            Let’s create a new story
          </div>

          <div className="text-md mt-6 flex aspect-square h-[40px] cursor-pointer flex-col items-center justify-center rounded-full bg-black text-white shadow-md">
            {isLoading ? (
              <Spinner className="text-white" size={24} />
            ) : (
              <span className="block h-[15px] text-2xl font-medium leading-[13px]">
                &#xFF0B;
              </span>
            )}
          </div>
        </button>
      </motion.div>
    );
  }

  return (
    <motion.button
      transition={{ duration: 0.25 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-md flex h-[35px] w-[35px] cursor-pointer flex-col items-center justify-center rounded-full text-white shadow-md disabled:cursor-not-allowed disabled:!bg-grey dark:text-mineShaftGrey sm:h-[40px] sm:w-[40px]"
      style={{ backgroundImage: gradient }}
      onClick={createNewStory}
      aria-label="Create new Story"
      disabled={isLoading}
    >
      {isLoading ? (
        <Spinner
          className={isDarkMode ? 'text-black' : 'text-white'}
          size={24}
        />
      ) : (
        <span className="block h-[15px] text-2xl font-medium leading-[13px]">
          &#xFF0B;
        </span>
      )}
    </motion.button>
  );
};
